import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { CustomSwitch } from "components/common/Switch";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import ErrorMessage from "components/common/ErrorMessage";
import { setError } from "slices/typeSlice";
import { updateType } from "slices/typeSlice";
import { addType } from "slices/typeSlice";
import { typesSelector } from "slices/typeSlice";
import { Controller, FormProvider, useForm } from "react-hook-form";
import PhoneInput from "components/common/PhoneInput";
import { useNavigate } from "react-router-dom";
import FullPageLoader from "components/common/FullPageLoader";
import LanguageTiers from "./InterpreterRate";
import { usePermissions } from "layouts/Admin";
import InterpreterRate from "./InterpreterRate";
import { AsyncPaginate } from "react-select-async-paginate";
import { selectCustomStyles } from "components/common/Select";
import { fetchLanguages } from "slices/languageSlice";

const AccountForm = ({ item }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { checkPermission } = usePermissions();

  const methods = useForm({});
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    control,
    setValue,
    clearErrors,
    resetField,
  } = methods;
  const { loader, error } = useSelector(typesSelector);
  const [status, setStatus] = useState(1);
  const [credentialsSend, setCredentialsSend] = useState(1);
  const [notification, setNotification] = useState(1);
  const [vriOnDemandLanguageState, setVriOnDemandLanguageState] = useState([]);
  const [vriPrescheduledLanguageState, setVriPrescheduledLanguageState] =
    useState([]);

  const resetAllFields = () => {
    setValue("name", "");
    setValue("email", "");
    setValue("password", "");
    setValue("phone", "");
    setNotification(1);

    setStatus(1);
  };

  useEffect(() => {
    setValue("name", item?.name || "");
    setValue("email", item?.email || "");
    setValue("password", "");
    setValue("phone", item?.phone || "");
    setValue(
      "spanish_translation_rate",
      item?.interpretation_rates?.spanish_translation_rate || ""
    );
    setValue(
      "other_translation_rate",
      item?.interpretation_rates?.other_translation_rate || ""
    );
    setValue(
      "spanish_formatting_rate",
      item?.interpretation_rates?.spanish_formatting_rate || ""
    );
    setValue(
      "other_formatting_rate",
      item?.interpretation_rates?.other_formatting_rate || ""
    );

    setStatus(typeof item?.status == "number" ? item?.status : 1);
    setCredentialsSend(
      typeof item?.user?.credentials_send == "number"
        ? item?.user?.credentials_send
        : 1
    );
    setNotification(
      typeof item?.user?.notifications == "number"
        ? item?.user?.notifications
        : 1
    );

    setVriOnDemandLanguageState(
      item?.vri_on_demand_languages?.map((val) => ({
        label: val?.language?.name || "",
        value: val?.language?.id || "",
      })) || []
    );

    setVriPrescheduledLanguageState(
      item?.vri_prescheduled_languages?.map((val) => ({
        label: val?.language?.name || "",
        value: val?.language?.id || "",
      })) || []
    );

    setValue(
      "vri_on_demand_language_ids",
      item?.vri_on_demand_languages?.map((language) => language.language_id) || []
    );

    setValue(
      "vri_prescheduled_language_ids",
      item?.vri_prescheduled_languages?.map(
        (language) => language.language_id
      ) || []
    );

    if (item?.vri_on_demand_language_ids && item?.vri_on_demand_language_ids.length > 0) {
      loadLanguagesByIds(item.vri_on_demand_language_ids).then((options) => {
        setVriOnDemandLanguageState(options);
        setValue("vri_on_demand_language_ids", item.vri_on_demand_language_ids);
      });
    }

    if (item?.vri_prescheduled_language_ids && item?.vri_prescheduled_language_ids.length > 0) {
      loadLanguagesByIds(item.vri_prescheduled_language_ids).then((options) => {
        setVriPrescheduledLanguageState(options);
        setValue("vri_prescheduled_language_ids", item.vri_prescheduled_language_ids);
      });
    }

    dispatch(setError({}));

    return () => {
      resetAllFields();
    };
  }, [item]);

  console.log("items", item);

  const submitCallback = (status, message) => {
    if (status) {
      toast.success(message, {
        className: "successToast",
      });
      resetAllFields();
      navigate(-1);
    } else {
      console.log("onSubmit", error);
      toast.error(message, {
        className: "errorToast",
      });
    }
  };

  const loadLanguages = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(
      fetchLanguages(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );

    const options = result?.data || (Array.isArray(result) ? result : []);
    const lastPage = result?.last_page || 1;

    return {
      options: options.map((language) => ({
        value: language.id,
        label: language.name,
      })),
      hasMore: page < lastPage,
      additional: {
        page: page + 1,
      },
    };
  };

  const loadLanguagesByIds = async (ids) => {
    if (!ids || ids.length === 0) return [];
    const result = await dispatch(
      fetchLanguages(
        1,
        {
          ids: ids,
          pageLength: ids.length,
        },
        true,
        true
      )
    );
    const options = result?.data || (Array.isArray(result) ? result : []);
    return options.map((language) => ({
      value: language.id,
      label: language.name,
    }));
  };

  const onSubmit = (data) => {
    dispatch(setError({}));
    let newData = {
      ...data,
      status,
      credentialsSend,
      notification,
    };

    // Ensure the 4 rate fields are appended as numbers when creating
    if (!item?.id) {
      newData = {
        ...newData,
        spanish_translation_rate: Number(data.spanish_translation_rate),
        other_translation_rate: Number(data.other_translation_rate),
        spanish_formatting_rate: Number(data.spanish_formatting_rate),
        other_formatting_rate: Number(data.other_formatting_rate),
      };
      dispatch(addType(newData, submitCallback));
    } else {
      dispatch(updateType(newData, item?.id, submitCallback));
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <form className="card_table p-4" onSubmit={handleSubmit(onSubmit)}>
          {/* Main credientials area  */}
          <div className="foam_page_row">
            <div className="row-primary">
              <h2>Name *</h2>
              <div className="w-100">
                <Controller
                  name="name"
                  rules={{
                    required: "Organization name is required",
                  }}
                  control={control}
                  render={({ field }) => (
                    <input
                      className="input-in type_inp"
                      type="text"
                      name="name"
                      placeholder="Name"
                      {...field}
                    />
                  )}
                />
                {error?.errors?.name?.map((error) => (
                  <div style={{ marginTop: 10 }}>
                    <ErrorMessage text={error} />
                  </div>
                ))}
                {errors?.name?.message && (
                  <div style={{ marginBottom: 10 }}>
                    <ErrorMessage text={errors?.name?.message} />
                  </div>
                )}
              </div>
            </div>

            <div className="row-primary">
              <h2>Email *</h2>
              <div className="w-100">
                <Controller
                  name="email"
                  rules={{
                    required: "Email is required",
                  }}
                  control={control}
                  render={({ field }) => (
                    <input
                      className={`input-in type_inp ${error?.errors?.email ? "border-red" : ""
                        } `}
                      type="email"
                      autoComplete="off"
                      name="email"
                      pattern={
                        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
                      }
                      placeholder="Email Address"
                      {...field}
                    />
                  )}
                />
                {error?.errors?.email?.map((error) => (
                  <div style={{ marginTop: 10 }}>
                    <ErrorMessage text={error} />
                  </div>
                ))}
                {errors?.email?.message && (
                  <div style={{ marginBottom: 10 }}>
                    <ErrorMessage text={errors?.email?.message} />
                  </div>
                )}
              </div>
            </div>

            <div className="row-primary">
              <h2>Password {!item && "*"}</h2>
              <div className="w-100">
                <Controller
                  name="password"
                  rules={
                    !item && {
                      required: "Password is required",
                    }
                  }
                  control={control}
                  render={({ field }) => (
                    <input
                      className={`input-in type_inp `}
                      autoComplete="off"
                      type="password"
                      name="password"
                      placeholder="Password"
                      {...field}
                      disabled={watch("type") == "interpreter" ? true : false}
                    />
                  )}
                />
                {error?.errors?.password?.map((error) => (
                  <div style={{ marginTop: 10 }}>
                    <ErrorMessage text={error} />
                  </div>
                ))}
                {errors?.password?.message && (
                  <div style={{ marginBottom: 10 }}>
                    <ErrorMessage text={errors?.password?.message} />
                  </div>
                )}
              </div>
            </div>

            <div className="row-primary">
              <h2>Phone</h2>
              <div className="w-100">
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <PhoneInput
                      className={`input-in type_inp ${error?.errors?.phone ? "border-red" : ""
                        } `}
                      type="tel"
                      name="contact"
                      placeholder="Phone number"
                      {...field}
                    />
                  )}
                />
                {error?.errors?.phone?.map((error) => (
                  <div style={{ marginTop: 10 }}>
                    <ErrorMessage text={error} />
                  </div>
                ))}
              </div>
            </div>

            <div className="row-primary align-items-center">
              <h2 className="m-0">Credentials will be sent over email</h2>
              <label className="custom-checkbox checkbox">
                <input
                  type="checkbox"
                  name="credentials_send"
                  checked={credentialsSend === 1 ? true : false}
                  onChange={(e) => setCredentialsSend(e.target.checked ? 1 : 0)}
                />
                <span className="checkmark"></span>
              </label>
            </div>

            <div className="row-primary align-items-center">
              <h2 className="mt-0">
                Notifications{" "}
                <span className="opacity-50">
                  (all type of notifications regarding appointments)
                </span>
              </h2>
              <CustomSwitch
                labelText="notifications"
                checked={notification == 1 ? true : false}
                handleChange={(checked) => setNotification(checked ? 1 : 0)}
              />
            </div>
          </div>

          <InterpreterRate
            control={control}
            error={error}
            errors={errors}
            item={item}
            dispatch={dispatch}
            setValue={setValue}
            watch={watch}
          />

          <div className="row-primary">
            <div className="row-primary mt-4 w-100">
              <h2>VRI on demand languages</h2>
              <Controller
                name="vri_on_demand_language_ids"
                control={control}
                render={({ field }) => (
                  <div style={{ width: "100%" }}>
                    <AsyncPaginate
                      menuPlacement="auto"
                      isMulti
                      styles={{
                        ...selectCustomStyles,
                      }}
                      value={vriOnDemandLanguageState}
                      loadOptions={loadLanguages}
                      isClearable
                      escapeClearsValue
                      onChange={(selected) => {
                        setVriOnDemandLanguageState(selected);
                        field.onChange(selected.map((opt) => opt.value));
                      }}
                      additional={{
                        page: 1,
                      }}
                    />
                    {errors?.vri_on_demand_language_ids?.message && (
                      <div style={{ marginBottom: 10 }}>
                        <ErrorMessage
                          text={errors?.vri_on_demand_language_ids?.message}
                        />
                      </div>
                    )}
                  </div>
                )}
              />
            </div>

            <div className="row-primary mt-4 w-100">
              <h2>VRI prescheduled language</h2>
              <Controller
                name="vri_prescheduled_language_ids"
                control={control}
                render={({ field }) => (
                  <div style={{ width: "100%" }}>
                    <AsyncPaginate
                      menuPlacement="auto"
                      isMulti
                      styles={{
                        ...selectCustomStyles,
                      }}
                      value={vriPrescheduledLanguageState}
                      loadOptions={loadLanguages}
                      isClearable
                      escapeClearsValue
                      onChange={(selected) => {
                        setVriPrescheduledLanguageState(selected);
                        field.onChange(selected.map((opt) => opt.value));
                      }}
                      additional={{
                        page: 1,
                      }}
                    />
                    {errors?.vri_prescheduled_language_ids?.message && (
                      <div style={{ marginBottom: 10 }}>
                        <ErrorMessage
                          text={errors?.vri_prescheduled_language_ids?.message}
                        />
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          <hr></hr>
          <h2 className="text-center my-3 fw-bold fst-italic">
            Translation Rates
          </h2>
          <div
            className="row-primary"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginTop: 20,
              marginBottom: 50,
            }}
          >
            <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                <span style={{ color: "#6c7a89" }}>
                  Spanish translation rate
                </span>
              </div>
              <div style={{ width: "36%" }}>
                <Controller
                  name="spanish_translation_rate"
                  control={control}
                  render={({ field }) => (
                    <input
                      className="input-in type_inp"
                      type="number"
                      min={0}
                      step={0.01}
                      name="normal_start_time"
                      required
                      placeholder="Spanish translation rate"
                      style={{
                        width: "100%",
                        minHeight: 40,
                        marginBottom: 0,
                      }}
                      {...field}
                    />
                  )}
                />
                {errors?.spanish_translation_rate?.message && (
                  <div style={{ marginBottom: 10 }}>
                    <ErrorMessage
                      text={errors?.spanish_translation_rate?.message}
                    />
                  </div>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                <span style={{ color: "#6c7a89" }}>Other translation rate</span>
              </div>
              <div style={{ width: "36%" }}>
                <Controller
                  name="other_translation_rate"
                  control={control}
                  render={({ field }) => (
                    <input
                      className="input-in type_inp"
                      type="number"
                      min={0}
                      step={0.01}
                      name="normal_end_time"
                      required
                      placeholder="Other translation rate"
                      style={{
                        width: "100%",
                        minHeight: 40,
                        marginBottom: 0,
                      }}
                      {...field}
                    />
                  )}
                />
                {errors?.other_translation_rate?.message && (
                  <div style={{ marginBottom: 10 }}>
                    <ErrorMessage
                      text={errors?.other_translation_rate?.message}
                    />
                  </div>
                )}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: 24,
                alignItems: "center",
                marginTop: 24,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                <span style={{ color: "#6c7a89" }}>
                  Spanish formatting rate
                </span>
              </div>
              <div style={{ width: "36%" }}>
                <Controller
                  name="spanish_formatting_rate"
                  control={control}
                  render={({ field }) => (
                    <input
                      className="input-in type_inp"
                      type="number"
                      min={0}
                      step={0.01}
                      name="after_start_time"
                      required
                      placeholder="Spanish formatting rate"
                      style={{
                        width: "100%",
                        minHeight: 40,
                        marginBottom: 0,
                      }}
                      {...field}
                    />
                  )}
                />
                {errors?.spanish_formatting_rate?.message && (
                  <div style={{ marginBottom: 10 }}>
                    <ErrorMessage
                      text={errors?.spanish_formatting_rate?.message}
                    />
                  </div>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                <span style={{ color: "#6c7a89" }}>Other formatting rate</span>
              </div>
              <div style={{ width: "36%" }}>
                <Controller
                  name="other_formatting_rate"
                  control={control}
                  render={({ field }) => (
                    <input
                      className="input-in type_inp"
                      type="number"
                      min={0}
                      step={0.01}
                      name="after_end_time"
                      required
                      placeholder="Other formatting rate"
                      style={{
                        width: "100%",
                        minHeight: 40,
                        marginBottom: 0,
                      }}
                      {...field}
                    />
                  )}
                />
                {errors?.other_formatting_rate?.message && (
                  <div style={{ marginBottom: 10 }}>
                    <ErrorMessage
                      text={errors?.other_formatting_rate?.message}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="Form-footer footer_fullpage">
            <div className="row-primary m-0 align-items-center">
              {checkPermission("Status Client Account") && (
                <>
                  <h2 className="mt-0">Status</h2>
                  <CustomSwitch
                    labelText="active"
                    checked={status == 1 ? true : false}
                    handleChange={(checked) => setStatus(checked ? 1 : 0)}
                  />
                </>
              )}
            </div>

            <SubmitButton
              loader={loader}
              btnText={item?.id ? "Update" : "Create"}
            />
          </div>
        </form>
      </FormProvider>

      {loader && <FullPageLoader />}
    </>
  );
};

export default AccountForm;
